import React, { useEffect, useRef } from "react";
import { Formik, Form, FormikHelpers, useFormikContext } from "formik";
import * as Yup from "yup";
import { VStack } from "@chakra-ui/react";
import _get from "lodash.get";

import { showToast } from "../../utils/helpers";
import { DocumentFormStyled } from "./styled";
import { FormHeading } from "../FormHeading";
import { FileUpload } from "../FormInputs/FileUpload";
import { DocumentCategory } from "../FormInputs/DocumentCategory";
import { DocumentSubCategory } from "../FormInputs/DocumentSubCategory";
import { RelevantYear } from "../FormInputs/RelevantYear";
import { Currency } from "../FormInputs/Currency";
import { Submit } from "../FormInputs/Submit";
import { OnSubmitValidationError } from "../../utils/OnSubmitValidationError";
import { FormikInputWithLabel } from "newui/components/ui/InputWithLabel/FormikInputWithLabel";
import { DateInputWithLabel } from "newui/components/ui/InputWithLabel/DateInputWithLabel";
import { FormikCheckboxWithLabel } from "newui/components/ui/CheckboxWithLabel";

const CATEGORIES_WITH_NAME_FIELD = [1, 2, 3];
const CATEGORIES_WITH_SUBCATEGORY = [4, 5, 6, 7];
const OTHER_OHS_SUBCATEGORY_ID = 21;
const INSURANCE_CATEGORY_ID = 4;
const FINANCIAL_CATEGORY_ID = 3;
const REGISTRATION_CATEGORY_ID = 2;
const INDIVIDUAL_PROFILE_TYPE_ID = 3;

const SUBCATEGORY_LABELS: Record<number, string> = {
	5: "Our Occupational Health and Safety Management System conforms to the requirements of",
	6: "Our Company Policies and Statements Include",
	7: "Our Quality Management System conforms to the requirements of",
};

const STATIC_FIELD_LABELS: Record<string, string> = {
	category_id: "Category",
	sub_category_id: "Sub Category",
	file: "File",
	"extra_info.expiry_date": "Expiry Date",
	"extra_info.value": "Value",
	"extra_info.relevant_year": "Relevant Year",
	"extra_info.annual_turnover": "Annual Turnover",
	"extra_info.audited": "Audited",
};

const getFieldLabels = (
	values: DocumentFormValues
): Record<string, string> => ({
	...STATIC_FIELD_LABELS,
	file:
		Number(values.category_id) === REGISTRATION_CATEGORY_ID
			? "Evidence of Registration / Certification"
			: "File",
	sub_category_id:
		SUBCATEGORY_LABELS[Number(values.category_id)] || "Sub Category",
	name:
		Number(values.category_id) === REGISTRATION_CATEGORY_ID
			? "Name of Body / Agency registered with"
			: "Document Name",
});

const humanizeFieldKey = (
	key: string,
	labels: Record<string, string>
): string => {
	if (labels[key]) return labels[key];
	const leaf = key.split(".").pop() || key;
	return leaf
		.replace(/_/g, " ")
		.replace(/\b\w/g, (c) => c.toUpperCase());
};

const collectErrorFieldPaths = (
	errors: Record<string, unknown>,
	prefix = ""
): string[] => {
	const paths: string[] = [];
	for (const key of Object.keys(errors)) {
		const value = errors[key];
		const path = prefix ? `${prefix}.${key}` : key;
		if (value && typeof value === "object" && !Array.isArray(value)) {
			paths.push(...collectErrorFieldPaths(value as Record<string, unknown>, path));
		} else {
			paths.push(path);
		}
	}
	return paths;
};

type DocumentFormValues = {
	category_id: number | string;
	sub_category_id?: { id: number; name: string } | string | null;
	[key: string]: unknown;
	documentable_type?: string;
	extra_info?: Record<string, unknown>;
	file?: unknown;
	name?: string;
};

interface DocumentFormProps {
	document?: unknown;
	initialValues: DocumentFormValues;
	lockCategory?: boolean;
	lockedSubcategoryId?: number | null;
	lockSubcategory?: boolean;
	profileTypeId?: number;
	title?: string;
	handleSubmit: (
		values: DocumentFormValues,
		helpers: FormikHelpers<DocumentFormValues>
	) => void | Promise<void>;
}

const shouldShowNameField = (
	categoryId: number | string | undefined,
	subCategoryId: DocumentFormValues["sub_category_id"]
): boolean => {
	if (!categoryId) return false;
	const cat = Number(categoryId);
	if (CATEGORIES_WITH_NAME_FIELD.includes(cat)) return true;
	if (
		CATEGORIES_WITH_SUBCATEGORY.includes(cat) &&
		subCategoryId &&
		typeof subCategoryId === "object" &&
		subCategoryId.id === OTHER_OHS_SUBCATEGORY_ID
	) {
		return true;
	}
	return false;
};

const validationSchema = Yup.object().shape({
	category_id: Yup.mixed().test(
		"category-required",
		"Category is required",
		(value) => value !== "" && value !== null && value !== undefined
	),
	sub_category_id: Yup.mixed().when("category_id", {
		is: (category_id: number | "") =>
			CATEGORIES_WITH_SUBCATEGORY.includes(Number(category_id)),
		then: Yup.object()
			.shape({
				id: Yup.number().required(),
				name: Yup.string().required(),
			})
			.typeError("Sub category is required")
			.required("Sub category is required"),
		otherwise: Yup.mixed().notRequired(),
	}),
	name: Yup.string().when(["category_id", "sub_category_id"], {
		is: (category_id: number | "", sub_category_id: any) =>
			shouldShowNameField(category_id, sub_category_id),
		then: Yup.string().trim().required("Document name is required"),
		otherwise: Yup.string().notRequired(),
	}),
	file: Yup.mixed().test(
		"file-required",
		"File is required",
		(value) => value !== "" && value !== null && value !== undefined
	),
});

const INSURANCE_EXTRA_INFO_KEYS = ["expiry_date", "value"];
const FINANCIAL_EXTRA_INFO_KEYS = ["relevant_year", "annual_turnover", "audited"];

const CategoryChangeEffect: React.FC = () => {
	const { values, setFieldValue } = useFormikContext<DocumentFormValues>();
	const categoryId = values.category_id;
	const previousCategoryRef = useRef(categoryId);

	useEffect(() => {
		if (previousCategoryRef.current === categoryId) return;
		previousCategoryRef.current = categoryId;

		const cat = Number(categoryId);
		const subCat = values.sub_category_id;
		const subCatId =
			subCat && typeof subCat === "object" ? subCat.id : undefined;

		if (
			!CATEGORIES_WITH_SUBCATEGORY.includes(cat) &&
			values.sub_category_id
		) {
			setFieldValue("sub_category_id", "");
		}

		const nameStillShown =
			CATEGORIES_WITH_NAME_FIELD.includes(cat) ||
			(CATEGORIES_WITH_SUBCATEGORY.includes(cat) &&
				subCatId === OTHER_OHS_SUBCATEGORY_ID);
		if (!nameStillShown && values.name) {
			setFieldValue("name", "");
		}

		const extraInfo = (values.extra_info || {}) as Record<string, unknown>;
		if (cat !== INSURANCE_CATEGORY_ID) {
			for (const key of INSURANCE_EXTRA_INFO_KEYS) {
				if (extraInfo[key] !== undefined && extraInfo[key] !== "") {
					setFieldValue(`extra_info[${key}]`, "");
				}
			}
		}
		if (cat !== FINANCIAL_CATEGORY_ID) {
			for (const key of FINANCIAL_EXTRA_INFO_KEYS) {
				if (extraInfo[key] !== undefined && extraInfo[key] !== "") {
					setFieldValue(`extra_info[${key}]`, "");
				}
			}
		}
	}, [categoryId, setFieldValue, values.sub_category_id, values.name, values.extra_info]);

	return null;
};

export const DocumentForm: React.FC<DocumentFormProps> = ({
	handleSubmit,
	initialValues,
	profileTypeId,
	title,
	lockCategory,
	lockSubcategory,
	lockedSubcategoryId,
}) => {
	return (
		<DocumentFormStyled>
			<Formik<DocumentFormValues>
				enableReinitialize
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ setFieldValue, isSubmitting, values, errors }) => {
					const categoryId = Number(values.category_id) || 0;
					const showSubCategory =
						CATEGORIES_WITH_SUBCATEGORY.includes(categoryId);
					const showNameField = shouldShowNameField(
						values.category_id,
						values.sub_category_id
					);
					const subCategoryLabel =
						SUBCATEGORY_LABELS[categoryId] || "Sub Category";

					const onSubmitValidationError = () => {
						const fieldLabels = getFieldLabels(values);
						const labels = collectErrorFieldPaths(
							errors as Record<string, unknown>
						).map((path) => humanizeFieldKey(path, fieldLabels));
						const message =
							labels.length > 0
								? `Please complete all required fields before saving: ${labels.join(", ")}`
								: "Please complete all required fields before saving";
						showToast(message, "Error");
					};

					return (
						<Form>
							<div className="container">
								<FormHeading text={title || "Document Detail"} />
								<VStack
									id="wrapper__document-form"
									spacing={4}
									align="stretch"
								>
									<DocumentCategory
										label="Category"
										required="true"
										name="category_id"
										setFieldValue={setFieldValue}
										defaultValue={initialValues.category_id}
										values={values}
										testId="category_selector"
										disabled={lockCategory}
									/>

									{showNameField && (
										<FormikInputWithLabel
											type="text"
											name="name"
											label={
												categoryId === REGISTRATION_CATEGORY_ID
													? "Name of Body / Agency registered with"
													: "Document Name"
											}
											required
											testId="document_name"
										/>
									)}

									{showSubCategory && (
										<DocumentSubCategory
											label={subCategoryLabel}
											required="true"
											name="sub_category_id"
											setFieldValue={setFieldValue}
											defaultValue={initialValues.sub_category_id}
											values={values}
											selectedCategory={values.category_id}
											disabled={lockSubcategory}
											lockedSubcategoryId={lockedSubcategoryId}
										/>
									)}

									{categoryId === INSURANCE_CATEGORY_ID && (
										<>
											<DateInputWithLabel
												name="extra_info[expiry_date]"
												label="Expiry Date"
												value={
													_get(values, "extra_info[expiry_date]")
														? new Date(
															_get(
																values,
																"extra_info[expiry_date]"
															) as string
														)
														: null
												}
												onChange={(date: Date | null) => {
													if (date) {
														setFieldValue(
															"extra_info[expiry_date]",
															date.toISOString()
														);
													}
												}}
											/>
											<Currency
												label="Value"
												required={false}
												name="extra_info[value]"
												type="number"
												lang="en_EN"
												setFieldValue={setFieldValue}
												values={values}
											/>
										</>
									)}

									{categoryId === FINANCIAL_CATEGORY_ID && (
										<>
											<RelevantYear
												name="extra_info[relevant_year]"
												required={false}
												setFieldValue={setFieldValue}
												defaultValue={
													initialValues.extra_info?.relevant_year
												}
											/>
											{profileTypeId !== INDIVIDUAL_PROFILE_TYPE_ID && (
												<Currency
													label="Annual Turnover"
													required={false}
													name="extra_info[annual_turnover]"
													type="number"
													lang="en_EN"
													setFieldValue={setFieldValue}
													values={values}
												/>
											)}
											<FormikCheckboxWithLabel
												label="Audited"
												name="extra_info[audited]"
											/>
										</>
									)}

									{values.category_id !== "" && values.category_id != null && (
										<FileUpload
											label={
												categoryId === REGISTRATION_CATEGORY_ID
													? "Evidence of Registration / Certification"
													: "File"
											}
											required="true"
											multi={false}
											values={values}
											name="file"
											setFieldValue={setFieldValue}
										/>
									)}

									{values.category_id !== "" && values.category_id != null && (
										<Submit
											type="submit"
											isSubmitting={isSubmitting}
											text="Save to Profile"
											submittingText="Saving..."
										/>
									)}
								</VStack>
							</div>
							<CategoryChangeEffect />
							<OnSubmitValidationError callback={onSubmitValidationError} />
						</Form>
					);
				}}
			</Formik>
		</DocumentFormStyled>
	);
};
